import { addDays, format } from 'tz-date-fns'
import * as queryString from 'query-string'

import history from 'common/history'
import hostConfig from 'common/config'
import { axios } from 'common/utils'
import { isErrorRejected } from 'common/utils/ajax/apiErrorHandler'
import OrderServiceType from 'common/constants/order/orderServiceType'
import ProductOrderStatusType from 'common/constants/order/productOrderStatusType'
import SaleChannelType from 'common/constants/order/saleChannelType'
import ProductClassType from 'common/constants/order/productClassType'
import {
  ImageInspectionType,
  MONTH_REVIEW_PERIOD_END,
  NORMAL_REVIEW_PERIOD_END,
  REVIEW_DIVIDE_FETCH_SIZE,
} from 'common/constants/review'
import { ReviewAttachStatusType } from 'common/constants/review/statusType'
import { getReviewContentType, getReviewServiceType } from 'common/types/entities/api/report'
import {
  AdditionalPayInfo,
  PagedReviews,
  RepReviewAttachment,
  Review,
  ReviewAttachment,
  ReviewAttachmentForm,
  ReviewAttachmentType,
  ReviewEvaluationResult,
  ReviewIds,
  ReviewIdsParam,
  ReviewImageInspectionResult,
  ReviewPagingParams,
  ReviewProduct,
  ReviewUserInputForm,
} from 'common/types/entities/api/review'
import { range } from 'common/utils/range'
import { ArrayUtils } from '@shopping/common'
import { getReviewsSetLikes } from 'common/services/like'
import { getHeaders } from 'common/utils/ajax/axios'

interface EvaluationParam {
  leafCategoryId: string
  merchantNo: string
  originProductNo: string
}

export const fetchEvaluations = async (params: EvaluationParam) => {
  const response = await axios.get<ReviewEvaluationResult>('/v1/reviews/evaluations-result', {
    params,
  })
  return response && response.data
}

export const fetchMyDetails = async (reviewIds: number[]): Promise<Review[]> => {
  const response = await axios.get<Review[]>(`/v1/reviews/my-details`, {
    params: {
      reviewIds,
    },
  })
  return response && response.data
}

export const fetchDetails = async (reviewIds: number[], headers: any, merchantNo?: string): Promise<Review[]> => {
  const response = await axios.get<Review[]>(`/v1/reviews/details`, {
    params: {
      reviewIds,
      merchantNo,
    },
    ...getHeaders(headers),
  })

  return response && response.data
}

export const fetchPagedMyReviews = async (params: ReviewPagingParams, headers: any) => {
  const response = await axios.get<PagedReviews>('/v1/reviews/paged-my-reviews', {
    params,
    ...getHeaders(headers),
  })
  return response && response.data
}

export const fetchPagedMyWritableReviews = async (params: { page: number; rowsPerPage: number }, headers: any) => {
  const response = await axios.get('/v1/pay/writable-reviews', {
    params,
    ...getHeaders(headers),
    data: {
      [isErrorRejected]: true,
    },
  })
  return response && response.data
}

export const fetchPagedReviews = async (params: ReviewPagingParams, headers?: any) => {
  const response = await axios.get<PagedReviews>('/v1/reviews/paged-reviews', {
    params,
    ...getHeaders(headers),
  })
  return response && response.data
}

export const fetchReviewIds = async (params: ReviewIdsParam): Promise<ReviewIds> => {
  const response = await axios.get<ReviewIds>(`/v1/reviews/attaches/ids-count`, {
    params,
  })
  return response && response.data
}

export const fetchPhotoVideos = async (reviewIds: number[], merchantNo: string): Promise<RepReviewAttachment[]> => {
  const response = await axios.get<RepReviewAttachment[]>(`/v1/reviews/attachments`, {
    params: {
      reviewIds,
      merchantNo,
    },
  })
  return response && response.data
}

export const fetchAdditionalInfoFromPay = async (
  productOrderIds: string,
  resultSet: string,
): Promise<AdditionalPayInfo> => {
  const response = await axios.get<AdditionalPayInfo>(`/v1/reviews/additional-pay`, {
    params: {
      productOrderIds,
      resultSet,
    },
  })
  return response && response.data
}

export const getStartDateOfMonthReview = (purchaseDecisionYmdt: number) => {
  return addDays(new Date(purchaseDecisionYmdt), NORMAL_REVIEW_PERIOD_END)
}

export const getEndDateOfMonthReview = (purchaseDecisionYmdt: number) => {
  return addDays(new Date(purchaseDecisionYmdt), MONTH_REVIEW_PERIOD_END - 1)
}

export const fetchReviewProducts = async (
  orderNo: string,
  productOrderNos?: string[],
  headers?: any,
): Promise<ReviewProduct[]> => {
  if (!orderNo || orderNo.length <= 0) {
    throw '주문 정보를 확인 할 수 없습니다.'
  }
  try {
    const response = await axios.get<ReviewProduct[]>(`/v1/reviews/orders/${orderNo}`, {
      ...getHeaders(headers),
      data: { [isErrorRejected]: true },
      params: { productOrderNos },
    })
    return response && response.data
  } catch (err) {
    throw (err && err.response && err.response.data && err.response.data.message) || '주문 정보를 확인 할 수 없습니다.'
  }
}

export const fetchReviewProductByReviewId = async (reviewId: number, headers?: string): Promise<ReviewProduct> => {
  try {
    const response = await axios.get<ReviewProduct>(`/v1/reviews/${reviewId}/orders`, {
      ...getHeaders(headers),
      data: { [isErrorRejected]: true },
    })
    return response && response.data
  } catch (err) {
    throw (err && err.response && err.response.data && err.response.data.message) || '리뷰 정보를 확인 할 수 없습니다.'
  }
}

export const registerReview = async (form: ReviewUserInputForm) => {
  try {
    const response = await axios.post('/v1/reviews', form, {
      data: { [isErrorRejected]: true },
    })
    return response && response.data
  } catch (err) {
    throw (err && err.response && err.response.data && err.response.data.message) || '리뷰 등록에 실패 했습니다.'
  }
}

export const modifyReview = async (form: ReviewUserInputForm) => {
  try {
    const response = await axios.put(`/v1/reviews/${form.id}`, form, {
      data: { [isErrorRejected]: true },
    })
    return response && response.data
  } catch (err) {
    throw (err && err.response && err.response.data && err.response.data.message) || '리뷰 수정에 실패 했습니다.'
  }
}

export const deleteReview = async (reviewId: number) => {
  try {
    const response = await axios.delete(`/v1/reviews/${reviewId}`, {
      data: { [isErrorRejected]: true },
    })
    return response && response.data
  } catch (err) {
    throw (err && err.response && err.response.data && err.response.data.message) || '리뷰 삭제에 실패 했습니다.'
  }
}

export const isOneMonthType = (reviewProduct: ReviewProduct): boolean => {
  return (reviewProduct && +Date.now() >= parseInt(format(reviewProduct.reviewWriteExpirationYmdt, 'x'), 10)) || false
}

/**
 * 한달리뷰 혜택 대상인지 여부
 *  - 일반리뷰를 작성한 이후 한달리뷰 작성이여야만 한달리뷰 혜택을 받으므로 구분하기 위함
 * @param reviewProduct
 */
export const isTypeOfOneMonthBenefits = (reviewProduct: ReviewProduct): boolean => {
  return isOneMonthType(reviewProduct) && !!reviewProduct.reviewCreateYmdt
}

export const filterWritableReviewProducts = (reviewProducts: ReviewProduct[]): ReviewProduct[] => {
  const MAX_REVIEW_PRODUCTS_LENGTH = 100
  let errorMessage = '리뷰를 작성 할 수 없습니다.'
  const filteredReviewProducts = reviewProducts.filter(reviewProduct => {
    const { validate, errors } = validateWritableReviewProduct(reviewProduct)
    if (!validate && errors && errors.length > 0) {
      errorMessage = errors[0]
    }
    return validate
  })
  if (filteredReviewProducts.length <= 0) {
    throw errorMessage
  }
  return filteredReviewProducts.slice(0, MAX_REVIEW_PRODUCTS_LENGTH)
}

interface Validation {
  validate: boolean
  errors: string[]
}

export const validateWritableReviewProduct = (reviewProduct: ReviewProduct): Validation => {
  const {
    serviceType,
    reviewCreateYmdt,
    afterUseReviewCreateYmdt,
    afterUseReviewWriteExpirationYmdt,
    productOrderStatusType,
    saleChannelType,
    productClassType,
  } = reviewProduct
  const isAfterUse = isOneMonthType(reviewProduct)
  const notWritten = (isAfterUse && !afterUseReviewCreateYmdt) || (!isAfterUse && !reviewCreateYmdt)
  if (!notWritten) {
    return {
      errors: ['이미 리뷰를 작성하셨습니다. 내가 작성한 리뷰를 확인해주세요.'],
      validate: false,
    }
  }
  if (
    afterUseReviewWriteExpirationYmdt &&
    +Date.now() >= parseInt(format(afterUseReviewWriteExpirationYmdt, 'x'), 10)
  ) {
    return {
      errors: ['리뷰 작성 기한이 종료되었습니다.'],
      validate: false,
    }
  }
  return validateWritableProductOrder(serviceType, productOrderStatusType, saleChannelType, productClassType)
}

export const validateWritableProductOrder = (
  serviceType: OrderServiceType,
  productOrderStatusType: ProductOrderStatusType,
  saleChannelType: SaleChannelType,
  productClassType: ProductClassType,
): Validation => {
  const errors = []
  if (serviceType && !OrderServiceType.isReviewWritable(serviceType)) {
    errors.push(`${OrderServiceType.getName(serviceType)} 주문은 리뷰를 작성 할 수 없습니다.`)
  }

  if (productOrderStatusType && !ProductOrderStatusType.isReviewWritable(productOrderStatusType)) {
    errors.push(`${ProductOrderStatusType.getName(productOrderStatusType)} 상태인 주문은 리뷰를 작성 할 수 없습니다.`)
  }

  if (saleChannelType && !SaleChannelType.isReviewWritable(saleChannelType)) {
    errors.push(`${SaleChannelType.getName(saleChannelType)} 상품 주문은 리뷰를 작성 할 수 없습니다.`)
  }

  if (productClassType && !ProductClassType.isReviewWritable(productClassType)) {
    errors.push(`${ProductClassType.getName(productClassType)} 주문은 리뷰를 작성 할 수 없습니다.`)
  }
  return {
    errors,
    validate: errors.length < 1,
  }
}

export const getFullAttachmentPath = (reviewAttachment: ReviewAttachmentForm) => {
  const { reviewAttachmentType, attachDirectoryName, attachName } = reviewAttachment
  const { imageUrl, videoUrl } = hostConfig
  return reviewAttachmentType === 'M'
    ? `${videoUrl.domain}${attachDirectoryName}/${attachName}`
    : `${imageUrl.domain}${attachDirectoryName}/${attachName}`
}

export const getMerchantChannelServiceTypeString = (merchantChannelServiceType: string): string => {
  switch (merchantChannelServiceType) {
    case 'STOREFARM':
      return '스마트스토어'
    case 'SHOPPINGWINDOW':
      return '쇼핑윈도'
    default:
      return null
  }
}

export const confirmFormChanging = (isFormChanging: boolean): boolean => {
  return isFormChanging && !window.confirm('입력하신 정보가 저장되지 않습니다. 정말 다음에 작성하시겠습니까?')
}

export const getReturnUrlWithIsCancel = (returnUrl: string, isCancel: boolean) => {
  const [path, params] = returnUrl.split('?')
  const queries = queryString.stringify({ ...queryString.parse(params), isCancel })
  return `${path}?${queries}`
}

export const moveToReturnUrl = (returnUrl: string, isCancel: boolean) => {
  if (returnUrl) {
    const url = (() => {
      const isNaverAppScheme = returnUrl.indexOf('naverapp://') >= 0
      return (isNaverAppScheme && returnUrl) || getReturnUrlWithIsCancel(returnUrl, isCancel)
    })()
    const isSameOrigin = url.indexOf(window.location.origin) >= 0
    if (isSameOrigin) {
      history.push(url.replace(window.location.origin, ''))
    } else {
      window.location.href = url
    }
  } else {
    window.close()
  }
}

export const isOnReportProcess = async (reviewProduct: ReviewProduct) => {
  const { id, reviewType, reviewContentClassType } = reviewProduct
  try {
    const response = await axios.get(`/v1/reports/reviews/${id}`, {
      params: {
        contentType: getReviewContentType(reviewContentClassType, reviewType),
        serviceType: getReviewServiceType(reviewProduct),
      },
    })
    if (response && response.data[0]) {
      if (response.data[0].reportYn === 'Y') {
        throw '신고 접수된 리뷰입니다. 신고 중에는 수정/삭제하실 수 없습니다.'
      }
      return false
    } else {
      throw 'PMON API 조회가 실패하여 삭제/수정이 실패하였습니다.'
    }
  } catch (err) {
    throw err
  }
}

/**
 * 리뷰 첨부 이미지 검수
 * @param attachedImages
 * https://line-objects-internal.com/vincent-swagger/review_spam.htm#/review_spam/doCheck
 */
export const inspectAttachedImages = async (
  attachedImages: ReviewAttachmentForm[],
): Promise<ReviewImageInspectionResult[]> => {
  const result = []
  if (!attachedImages || !attachedImages.length) return result

  const inspected = attachedImages.map(
    async (image): Promise<ReviewImageInspectionResult> => {
      if (image.attachName && image.attachName.toLocaleLowerCase().startsWith('screenshot')) {
        return {
          image,
          type: ImageInspectionType.SCREENSHOT,
        }
      }
      const response = await axios.get(`/v1/reviews/image-spam-check?url=${getFullAttachmentPath(image)}`)
      if (response && response.data) {
        const filtered = response.data.filter(item => item.is_spam)
        if (filtered.length) {
          const type = (() => {
            if (filtered[0].type === 'monotone') return ImageInspectionType.MONOTONE
            if (filtered[0].type === 'screenshot') return ImageInspectionType.SCREENSHOT
            return ImageInspectionType.PASS
          })()
          return {
            image,
            type,
          }
        }
      }
      return {
        image,
        type: ImageInspectionType.PASS,
      }
    },
  )
  return Promise.all(inspected)
}

export const getDividedFetchedReviews = async (reviewIds: number[], merchantNo, headers: any) => {
  const results = await Promise.all(
    range(0, Math.ceil(reviewIds.length / REVIEW_DIVIDE_FETCH_SIZE) - 1, 1).map(index =>
      fetchDetails(
        reviewIds.slice(REVIEW_DIVIDE_FETCH_SIZE * index, REVIEW_DIVIDE_FETCH_SIZE * (index + 1)),
        headers,
        merchantNo,
      ),
    ),
  )
  const fetchedReviews = ArrayUtils.flatten(results)
  return await getReviewsSetLikes(fetchedReviews, headers)
}

export const someReviewAttachedImagesBlinded = (reviewAttaches: ReviewAttachment[]) => {
  return (
    ArrayUtils.isNotEmpty(reviewAttaches) &&
    reviewAttaches
      .filter(attach => attach.reviewAttachmentType === ReviewAttachmentType.I)
      .some(img => img.reviewAttachStatusType === ReviewAttachStatusType.BLIND)
  )
}

export const getReviewAttachesWithoutBlindImage = (reviewAttaches: ReviewAttachment[]) => {
  return (
    (ArrayUtils.isNotEmpty(reviewAttaches) &&
      reviewAttaches.filter(
        attach =>
          attach.reviewAttachmentType === ReviewAttachmentType.M ||
          (attach.reviewAttachmentType === ReviewAttachmentType.I &&
            attach.reviewAttachStatusType !== ReviewAttachStatusType.BLIND),
      )) ||
    []
  )
}
